import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Alert, Spinner } from 'react-bootstrap';
import { authHeaders } from '../../api';
import { ENDPOINTS } from '../../endpoints';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../context/FeedbackContext';
import './MasterTicketsManagement.css';

const STATUS_CLASS = {
  'Abierto':     'mt-badge--abierto',
  'En progreso': 'mt-badge--en-progreso',
  'Resuelto':    'mt-badge--resuelto',
  'Cerrado':     'mt-badge--cerrado',
};

const PRIORITY_CLASS = {
  'Urgente': 'mt-badge--urgente',
  'Alto':    'mt-badge--alto',
  'Medio':   'mt-badge--medio',
  'Bajo':    'mt-badge--bajo',
};

const TicketModal = ({ show, onHide, ticket, onUpdated, onSuccess }) => {
  const { user } = useAuth();
  const isMaster = user?.user_type === 'master';

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [masterUsers, setMasterUsers] = useState([]);
  const [priority, setPriority] = useState('');
  const [assignedOpr, setAssignedOpr] = useState('');
  const [loading, setLoading] = useState(false);
  const feedback = useFeedback();

  // ── Estado del modal de resolución ──────────────────────────────────────
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveAction, setResolveAction] = useState('');
  const [resolveComment, setResolveComment] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState('');

  // ── Estado del modal de rechazo ──────────────────────────────────────────
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState('');

  const loadComments = useCallback(async () => {
    if (!ticket) return;
    try {
      const res = await fetch(ENDPOINTS.TICKET_COMMENTS(ticket.ticket_id), {
        headers: authHeaders(),
      });
      if (res.ok) setComments(await res.json());
    } catch (e) {
      console.error('Error loading comments', e);
    }
  }, [ticket]);

  useEffect(() => {
    if (show && ticket) {
      loadComments();
      setPriority(ticket.priority || '');
      setAssignedOpr(ticket.assigned_opr_id || '');
      if (isMaster) loadMasterUsers();
    }
  }, [show, ticket, isMaster, loadComments]);

  const loadMasterUsers = async () => {
    try {
      const res = await fetch(ENDPOINTS.MASTER_USERS, { headers: authHeaders() });
      if (res.ok) setMasterUsers(await res.json());
    } catch (e) {
      console.error('Error loading master users', e);
    }
  };

  const handleAssign = async () => {
    if (!priority || !assignedOpr) return;
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.TICKET_ASSIGN(ticket.ticket_id), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority, assigned_opr_id: parseInt(assignedOpr) }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al asignar');
      onUpdated && onUpdated();
      loadComments();
      feedback.success('Ticket asignado y en progreso');
    } catch (e) {
      feedback.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openResolveModal = () => {
    setResolveAction('');
    setResolveComment('');
    setResolveError('');
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    if (resolveLoading) return;
    setShowResolveModal(false);
  };

  const handleConfirmResolve = async () => {
    if (!resolveAction) {
      setResolveError('Selecciona una acción antes de continuar.');
      return;
    }
    setResolveLoading(true);
    setResolveError('');
    try {
      // 1. Publicar comentario si hay texto
      if (resolveComment.trim()) {
        const commentContent = `[${resolveAction}] ${resolveComment.trim()}`;
        const commentRes = await fetch(ENDPOINTS.TICKET_COMMENTS(ticket.ticket_id), {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: commentContent, is_internal: false }),
        });
        if (!commentRes.ok) throw new Error('Error al guardar el comentario');
      }

      // 2. Cambiar estado a "Resuelto"
      const resolveRes = await fetch(ENDPOINTS.TICKET_RESOLVE(ticket.ticket_id), {
        method: 'PUT',
        headers: authHeaders(),
      });
      if (!resolveRes.ok) throw new Error((await resolveRes.json()).detail || 'Error al resolver');

      // 3. Notificar y cerrar ambos modales
      onUpdated && onUpdated();
      onSuccess?.('Ticket resuelto. A la espera de la aceptación del usuario.');
      setShowResolveModal(false);
      onHide();
    } catch (e) {
      setResolveError(e.message);
    } finally {
      setResolveLoading(false);
    }
  };

  const openRejectModal = () => {
    setRejectReason('');
    setRejectError('');
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    if (rejectLoading) return;
    setShowRejectModal(false);
  };

  const handleConfirmReject = async () => {
    setRejectLoading(true);
    setRejectError('');
    try {
      if (rejectReason.trim()) {
        const commentRes = await fetch(ENDPOINTS.TICKET_COMMENTS(ticket.ticket_id), {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: `[Rechazo] ${rejectReason.trim()}`, is_internal: false }),
        });
        if (!commentRes.ok) throw new Error('Error al guardar el motivo del rechazo');
      }
      const statusRes = await fetch(ENDPOINTS.TICKET_STATUS(ticket.ticket_id), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'En progreso' }),
      });
      if (!statusRes.ok) throw new Error((await statusRes.json()).detail || 'Error al rechazar');
      onUpdated && onUpdated();
      setShowRejectModal(false);
      onHide();
    } catch (e) {
      setRejectError(e.message);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.TICKET_STATUS(ticket.ticket_id), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al cambiar estado');
      onUpdated && onUpdated();
      onHide();
      feedback.success(`Estado actualizado a "${newStatus}"`);
    } catch (e) {
      feedback.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.TICKET_COMMENTS(ticket.ticket_id), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newComment, is_internal: isInternal }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al agregar comentario');
      setNewComment('');
      setIsInternal(false);
      loadComments();
    } catch (e) {
      feedback.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!ticket) return null;

  const canAssign    = isMaster && (ticket.status === 'Abierto' || ticket.status === 'En progreso');
  const canResolve   = isMaster && (ticket.status === 'Abierto' || ticket.status === 'En progreso');
  const canAcceptReject = !isMaster && ticket.status === 'Resuelto';
  const assignReady  = priority && assignedOpr;

  return (
    <>
    <Modal show={show} onHide={onHide} size="lg" centered>

      {/* ── Encabezado ─────────────────────────────────────────────────── */}
      <Modal.Header closeButton className="tm-header">
        <Modal.Title as="div" className="tm-title">
          <span>Ticket {ticket.ticket_number}</span>
          <span className={`mt-badge ${STATUS_CLASS[ticket.status] || ''}`}>
            {ticket.status}
          </span>
          {ticket.priority && (
            <span className={`mt-badge ${PRIORITY_CLASS[ticket.priority] || ''}`}>
              {ticket.priority}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* ── Cuerpo ─────────────────────────────────────────────────────── */}
      <Modal.Body className="tm-body">

        {/* Meta-información */}
        <div className="tm-meta-block">
          <div>
            <p className="tm-subject">{ticket.subject}</p>
            <div className="tm-meta-pills">
              <span className="tm-meta-pill">
                <strong>Categoría:</strong> {ticket.category}
              </span>
              <span className="tm-meta-dot">·</span>
              <span className="tm-meta-pill">
                <strong>Organización:</strong> {ticket.org_name}
              </span>
              <span className="tm-meta-dot">·</span>
              <span className="tm-meta-pill">
                <strong>Usuario:</strong> {ticket.creator_name}
              </span>
            </div>
          </div>
          <span className="tm-created-date">
            {new Date(ticket.created_at).toLocaleDateString('es-CL')}
          </span>
        </div>

        {/* Descripción */}
        <div className="tm-desc">
          <p className="tm-desc-label">Descripción</p>
          <p className="tm-desc-text">{ticket.description || ticket.subject}</p>
        </div>

        {/* Asignado a */}
        {ticket.assigned_name && (
          <div className="tm-assigned-row">
            <span className="tm-assigned-label">Asignado a:</span>
            <span className="tm-assigned-name">{ticket.assigned_name}</span>
          </div>
        )}

        {/* Sección de gestión — solo master */}
        {isMaster && (
          <div className="tm-management-section">
            <p className="tm-section-label">Gestión del ticket</p>
            <div className="tm-form-row">
              <div className="tm-form-group">
                <label className="tm-form-label">Prioridad</label>
                <select
                  className="tm-form-select"
                  value={priority}
                  onChange={e => setPriority(e.target.value)}
                  disabled={!canAssign}
                >
                  <option value="">Seleccionar prioridad</option>
                  <option value="Urgente">Urgente</option>
                  <option value="Alto">Alto</option>
                  <option value="Medio">Medio</option>
                  <option value="Bajo">Bajo</option>
                </select>
              </div>
              <div className="tm-form-group">
                <label className="tm-form-label">Asignar usuario</label>
                <select
                  className="tm-form-select"
                  value={assignedOpr}
                  onChange={e => setAssignedOpr(e.target.value)}
                  disabled={!canAssign}
                >
                  <option value="">Seleccionar usuario</option>
                  {masterUsers.map(u => (
                    <option key={u.opr_id} value={u.opr_id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="tm-action-buttons">
              {canAssign && (
                <button
                  className="tm-btn tm-btn--primary tm-btn--sm"
                  onClick={handleAssign}
                  disabled={!assignReady || loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Gestionar'}
                </button>
              )}
              {canResolve && (
                <button
                  className="tm-btn tm-btn--success tm-btn--sm"
                  onClick={openResolveModal}
                  disabled={loading}
                >
                  Resolver ticket
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sección aceptar / rechazar — usuario normal */}
        {canAcceptReject && (
          <div className="tm-resolution-section">
            <p className="tm-resolution-text">
              El ticket ha sido resuelto. ¿La solución fue satisfactoria?
            </p>
            <div className="tm-action-buttons">
              <button
                className="tm-btn tm-btn--success tm-btn--sm"
                onClick={() => handleStatusChange('Cerrado')}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Aceptar'}
              </button>
              <button
                className="tm-btn tm-btn--danger tm-btn--sm"
                onClick={openRejectModal}
                disabled={loading}
              >
                Rechazar
              </button>
            </div>
          </div>
        )}

        {/* Comentarios */}
        <div className="tm-comments-section">
          <p className="tm-comments-label">Comentarios</p>

          <div className="tm-comments-list">
            {comments.length === 0 ? (
              <div className="tm-comments-empty">Sin comentarios aún</div>
            ) : (
              comments.map(c => (
                <div
                  key={c.comment_id}
                  className={`tm-comment-item${c.is_internal ? ' tm-comment-item--internal' : ''}`}
                >
                  <div className="tm-comment-header">
                    <span className="tm-comment-author">{c.author_name}</span>
                    <span className="tm-comment-time">
                      {new Date(c.created_at).toLocaleString('es-CL')}
                      {c.is_internal && (
                        <span className="tm-internal-badge">Interno</span>
                      )}
                    </span>
                  </div>
                  <p className="tm-comment-text">{c.content}</p>
                </div>
              ))
            )}
          </div>

          {/* Agregar comentario */}
          <div className="tm-comment-form">
            <textarea
              className="tm-comment-textarea"
              rows={2}
              placeholder="Agregar comentario..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
            />
            {isMaster && (
              <label className="tm-internal-check">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={e => setIsInternal(e.target.checked)}
                />
                Comentario interno (solo visible para master)
              </label>
            )}
            <div className="tm-comment-footer">
              <button
                className="tm-btn tm-btn--outline-primary tm-btn--sm"
                onClick={handleAddComment}
                disabled={!newComment.trim() || loading}
              >
                {loading ? <Spinner size="sm" /> : 'Agregar comentario'}
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Modal.Footer className="tm-footer">
        <button className="tm-btn tm-btn--ghost" onClick={onHide}>
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>

    {/* ── Modal de resolución ─────────────────────────────────────────── */}
    <Modal
      show={showResolveModal}
      onHide={closeResolveModal}
      centered
      backdrop="static"
      size="md"
    >
      <Modal.Header closeButton className="tm-header tm-header--resolve">
        <Modal.Title as="div" className="tm-title">
          Resolver ticket {ticket.ticket_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="tm-resolve-body">
        {resolveError && (
          <Alert variant="danger" onClose={() => setResolveError('')} dismissible>
            {resolveError}
          </Alert>
        )}

        {/* Acción */}
        <div className="tm-resolve-form-group">
          <label className="tm-resolve-label">
            Acción<span className="tm-required">*</span>
          </label>
          <select
            className="tm-resolve-select"
            value={resolveAction}
            onChange={e => setResolveAction(e.target.value)}
            disabled={resolveLoading}
          >
            <option value="">Seleccionar acción...</option>
            <option value="Corrección aplicada">Corrección aplicada</option>
            <option value="Consulta respondida">Consulta respondida</option>
            <option value="Mejora registrada">Mejora registrada</option>
            <option value="Sin acción requerida">Sin acción requerida</option>
            <option value="No reproducible">No reproducible</option>
          </select>
        </div>

        {/* Comentario */}
        <div className="tm-resolve-form-group">
          <label className="tm-resolve-label">Comentario</label>
          <textarea
            className="tm-resolve-textarea"
            rows={4}
            placeholder="Describe brevemente cómo se resolvió el ticket o qué información se entregó al usuario..."
            value={resolveComment}
            onChange={e => setResolveComment(e.target.value)}
            disabled={resolveLoading}
          />
          <span className="tm-resolve-hint">
            Este comentario quedará visible para el usuario.
          </span>
        </div>
      </Modal.Body>

      <Modal.Footer className="tm-resolve-footer">
        <button
          className="tm-btn tm-btn--ghost"
          onClick={closeResolveModal}
          disabled={resolveLoading}
        >
          Cancelar
        </button>
        <button
          className="tm-btn tm-btn--success"
          onClick={handleConfirmResolve}
          disabled={!resolveAction || resolveLoading}
        >
          {resolveLoading
            ? <><Spinner size="sm" className="me-2" />Resolviendo...</>
            : 'Aceptar'
          }
        </button>
      </Modal.Footer>
    </Modal>

    {/* ── Modal de rechazo ────────────────────────────────────────────── */}
    <Modal
      show={showRejectModal}
      onHide={closeRejectModal}
      centered
      backdrop="static"
      size="md"
    >
      <Modal.Header closeButton className="tm-header tm-header--danger">
        <Modal.Title as="div" className="tm-title">
          Rechazar resolución — {ticket?.ticket_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="tm-resolve-body">
        {rejectError && (
          <Alert variant="danger" onClose={() => setRejectError('')} dismissible>
            {rejectError}
          </Alert>
        )}
        <div className="tm-resolve-form-group">
          <label className="tm-resolve-label">Motivo del rechazo</label>
          <textarea
            className="tm-resolve-textarea"
            rows={4}
            placeholder="Explica por qué la solución no fue satisfactoria o qué falta por resolver..."
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            disabled={rejectLoading}
          />
          <span className="tm-resolve-hint">
            Este comentario quedará visible para el equipo de soporte.
          </span>
        </div>
      </Modal.Body>

      <Modal.Footer className="tm-resolve-footer">
        <button
          className="tm-btn tm-btn--ghost"
          onClick={closeRejectModal}
          disabled={rejectLoading}
        >
          Cancelar
        </button>
        <button
          className="tm-btn tm-btn--danger"
          onClick={handleConfirmReject}
          disabled={rejectLoading}
        >
          {rejectLoading
            ? <><Spinner size="sm" className="me-2" />Enviando...</>
            : 'Confirmar rechazo'
          }
        </button>
      </Modal.Footer>
    </Modal>

    </>
  );
};

export default TicketModal;
